import { fromApiTournamentDate } from '@y-sports/shared';
import { getApiConfigNext } from '@y-sports/api-client';
import { normalizePublicTournamentTeam } from './publicTournamentTeams';
import { TournamentsApi } from '@y-sports/api-client/generated-next-management/src/apis/TournamentsApi';
import type {
  MatchesListResponse,
  TournamentClassification,
  TournamentListItem,
  TournamentResultsResponse,
  TournamentStatistics,
  TournamentStructure,
  TournamentSummary,
  TournamentTeamsSummaryResponse,
} from '@y-sports/api-client/generated-next-management/src/models';
import { Configuration as NextManagementConfiguration } from '@y-sports/api-client/generated-next-management/src/runtime';

export type PublicPlatformStats = {
  totalTournaments: number;
  totalTeams: number;
  totalMatches: number;
  totalPlayers: number;
};

export type PublicAdHocMatch = {
  id?: string;
  tournamentId?: string | null;
  teamA?: {
    id?: string;
    name?: string;
    details?: {
      homeColor?: string;
      home_color?: string;
      awayColor?: string;
      away_color?: string;
      visitorColor?: string;
      visitor_color?: string;
      goalkeeperColor?: string;
      goalkeeper_color?: string;
      goalkeepColor?: string;
      goalkeep_color?: string;
    } | null;
  } | null;
  teamB?: {
    id?: string;
    name?: string;
    details?: {
      homeColor?: string;
      home_color?: string;
      awayColor?: string;
      away_color?: string;
      visitorColor?: string;
      visitor_color?: string;
      goalkeeperColor?: string;
      goalkeeper_color?: string;
      goalkeepColor?: string;
      goalkeep_color?: string;
    } | null;
  } | null;
  label?: string | null;
  matchTime?: Date | string | null;
  streamingUrl?: string | null;
  teamAScore?: number | null;
  teamBScore?: number | null;
  status?: string | null;
  court?: {
    id?: string | null;
    name?: string | null;
  } | null;
  dynamic?: boolean | null;
};

export type PublicTournamentDetails = TournamentSummary & {
  status?: string;
  description?: string;
  infoHtml?: string;
  locationUrl?: string;
  infoUrl?: string;
  latitude?: number;
  longitude?: number;
  isInvitationOnly?: boolean;
  invitedEmails?: string[];
  allowedCountries?: string[];
  allowPaymentReceiptUpload?: boolean;
  hasTermsAndConditions?: boolean;
  termsAndConditionsFilename?: string;
};

const normalizeTournamentDetails = (tournament: TournamentSummary): PublicTournamentDetails => ({
  ...tournament,
  date: fromApiTournamentDate(tournament.date),
  endDate: fromApiTournamentDate(tournament.endDate ?? tournament.date),
  description: tournament.meta?.description || tournament.details?.infoHtml,
  status: tournament.details?.tournamentStatus,
  infoHtml: tournament.details?.infoHtml,
  locationUrl: tournament.details?.locationUrl,
  infoUrl: tournament.details?.infoUrl,
  latitude: tournament.details?.latitude,
  longitude: tournament.details?.longitude,
  isInvitationOnly: tournament.details?.isInvitationOnly,
  invitedEmails: tournament.details?.invitedEmails,
  allowedCountries: tournament.details?.allowedCountries,
  allowPaymentReceiptUpload: tournament.details?.allowPaymentReceiptUpload,
  hasTermsAndConditions: tournament.details?.hasTermsAndConditions,
  termsAndConditionsFilename: tournament.details?.termsAndConditionsFileName,
});

const getTournamentsApi = async (): Promise<TournamentsApi> => {
  const apiConfig = await getApiConfigNext();
  return new TournamentsApi(
    new NextManagementConfiguration({
      basePath: apiConfig.basePath,
      headers: apiConfig.headers,
    }),
  );
};

export const acceptedTeamCount = (teams?: { status?: string }[]) => teams?.filter(team => team.status?.toLowerCase() === 'accepted').length ?? 0;

export const PublicTournamentDataService = {
  getTournamentCategories: async (tournamentId: string) => {
    const api = await getTournamentsApi();
    return api.getTournamentCategories({ id: tournamentId });
  },
  getTeamStatistics: async (tournamentId: string, teamId: string) => {
    const api = await getTournamentsApi();
    return api.getTeamTournamentStatistics({ id: tournamentId, teamId });
  },
  getTournamentList: async (): Promise<TournamentListItem[]> => {
    const api = await getTournamentsApi();
    const payload = await api.getTournaments();
    return (payload.tournaments || []).map(tournament => ({ ...tournament, date: fromApiTournamentDate(tournament.date), endDate: fromApiTournamentDate(tournament.endDate ?? tournament.date) }));
  },

  getPlatformStats: async (): Promise<PublicPlatformStats> => {
    const tournaments = await PublicTournamentDataService.getTournamentList();
    const teamSummaries = await Promise.all(
      tournaments
        .filter((tournament) => !!tournament.id)
        .map((tournament) => PublicTournamentDataService.getTournamentTeamsSummary(tournament.id!)),
    );

    return {
      totalTournaments: tournaments.length,
      totalTeams: teamSummaries.reduce((sum, summary) => sum + acceptedTeamCount(summary.teams), 0),
      totalMatches: tournaments.reduce((sum, tournament) => sum + (tournament.matchesCount || 0), 0),
      totalPlayers: teamSummaries.reduce(
        (sum, summary) =>
          sum + (summary.teams || []).reduce((teamSum, team) => teamSum + (team.playersCount || 0), 0),
        0,
      ),
    };
  },

  getTournamentDetails: async (tournamentId: string): Promise<PublicTournamentDetails> => {
    const api = await getTournamentsApi();
    return normalizeTournamentDetails(await api.getTournamentById({ id: tournamentId }));
  },

  getTournamentTeamsSummary: async (tournamentId: string): Promise<TournamentTeamsSummaryResponse> => {
    const apiConfig = await getApiConfigNext();
    const response = await fetch(`${apiConfig.basePath}/tournaments/${encodeURIComponent(tournamentId)}/teams-summary`, {
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) throw new Error(`Failed to load tournament teams: ${response.status}`);
    const payload = await response.json() as { teams?: Record<string, unknown>[] };
    return { teams: (payload.teams || []).map(normalizePublicTournamentTeam) };
  },

  getTournamentScheduleSummary: async (tournamentId: string): Promise<MatchesListResponse> => {
    const apiConfig = await getApiConfigNext();
    const response = await fetch(`${apiConfig.basePath}/tournaments/${encodeURIComponent(tournamentId)}/schedule-summary`, {
      method: 'GET',
      headers: { Accept: 'application/json' },
    });

    if (!response.ok) {
      throw new Error(`Failed to load tournament schedule: ${response.status}`);
    }

    return response.json();
  },

  getPublicAdHocMatches: async (): Promise<PublicAdHocMatch[]> => {
    const apiConfig = await getApiConfigNext();
    const response = await fetch(`${apiConfig.basePath}/matches/ad-hoc/public`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to load ad-hoc matches: ${response.status}`);
    }

    return response.json();
  },

  getTournamentStructureSummary: async (tournamentId: string): Promise<TournamentStructure> => {
    const api = await getTournamentsApi();
    return api.getTournamentStructureSummary({ id: tournamentId });
  },

  getTournamentResultsSummary: async (tournamentId: string): Promise<TournamentResultsResponse> => {
    const api = await getTournamentsApi();
    return api.getTournamentResultsSummary({ id: tournamentId });
  },

  getTournamentClassifications: async (tournamentId: string): Promise<TournamentClassification[]> => {
    const api = await getTournamentsApi();
    return api.getTournamentClassifications({ id: tournamentId });
  },

  getTournamentStatistics: async (tournamentId: string, categoryId?: string): Promise<TournamentStatistics> => {
    const api = await getTournamentsApi();
    return api.getTournamentStatistics({ id: tournamentId, categoryId });
  },
};
